import {
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subHours,
  subMonths,
  subYears,
} from "date-fns";
import { dateToPit } from "src/utils/time";
import { getNowInUserTz, type UserTzContext } from "./TimeWindow.calendarPresets";
import { TIME_WINDOW_ALL_TIME, TIME_WINDOW_DEFAULT } from "./TimeWindow.constants";
import {
  isValidTimeWindowSelection,
  parseTimeWindowSelection,
} from "./TimeWindow.selection";

export type ResolvedTimeWindowBounds = {
  start_time?: string;
  end_time?: string;
};

const throughNow = (start: Date): ResolvedTimeWindowBounds => ({
  start_time: dateToPit(start),
});

const closedRange = (start: Date, end: Date): ResolvedTimeWindowBounds => ({
  start_time: dateToPit(start),
  end_time: dateToPit(end),
});

export const resolveTimeWindowSelection = (
  selection: string,
  ctx: UserTzContext
): ResolvedTimeWindowBounds => {
  const parsed = parseTimeWindowSelection(selection);
  if (!parsed) {
    return resolveTimeWindowSelection(TIME_WINDOW_DEFAULT, ctx);
  }

  if (parsed.kind === "named" && parsed.token === TIME_WINDOW_ALL_TIME) {
    return {};
  }

  if (parsed.kind === "range") {
    return closedRange(parsed.start, parsed.end);
  }

  if (parsed.kind === "named") {
    const now = getNowInUserTz(ctx);
    if (parsed.token === "yesterday") {
      const day = subDays(now, 1);
      return closedRange(startOfDay(day), endOfDay(day));
    }
    if (parsed.token === "previous-month") {
      const previousMonth = subMonths(now, 1);
      return closedRange(startOfMonth(previousMonth), endOfMonth(previousMonth));
    }
    // month-to-date
    return throughNow(startOfMonth(now));
  }

  // last-* uses the shared PIT clock (tz 0). TimeZoneSelector is display-only.
  const now = getNowInUserTz({ ...ctx, tz: 0 });
  const { n, unit } = parsed;
  if (unit === "hours") {
    return throughNow(subHours(now, n));
  }
  if (unit === "days") {
    return throughNow(subDays(startOfDay(now), n - 1));
  }
  if (unit === "months") {
    return throughNow(subMonths(now, n));
  }
  return throughNow(subYears(now, n));
};

/**
 * `timeWindow` is a client-side selection; the API only ever takes resolved
 * bounds. The key is therefore always dropped, even when a caller supplied its
 * own `start_time` (the backend rejects a non-numeric `timeWindow`).
 */
export const adaptTimeWindowBodyParams = (
  body: Record<string, unknown> = {},
  ctx: UserTzContext
): Record<string, unknown> => {
  if (!("timeWindow" in body)) {
    return body;
  }

  const { timeWindow, ...rest } = body;

  // A caller-provided range wins - do not resolve over it. An absent selection
  // stays absent, so the backend applies its own open-ended default.
  if (
    rest.start_time ||
    timeWindow === undefined ||
    timeWindow === null ||
    timeWindow === ""
  ) {
    return rest;
  }

  const selection = isValidTimeWindowSelection(timeWindow)
    ? timeWindow
    : TIME_WINDOW_DEFAULT;

  return {
    ...rest,
    ...resolveTimeWindowSelection(selection, ctx),
  };
};
